import {v7 as uuidv7} from "uuid";

export const GRID_COLS = 3;
export const GRID_ROW_HEIGHT = 30;
export const GRID_MARGIN_Y = 12;
export const MIN_ITEM_H = 3;
export const DEFAULT_ITEM_H = 4;
export const ADD_SLOT_ID = "__criteria_add_slot__";

export const SECTION_GRID_COLS = 1;
export const SECTION_DEFAULT_ITEM_H = 16;
export const SECTION_MIN_ITEM_H = 8;
export const SECTION_ADD_SLOT_ID = "__criteria_section_add_slot__";

// Keep tool keys in sync with EvaluationTemplate::TOOLS and evaluation_template__structure.json
export const EVALUATION_TOOL_KEYS = [
  "moment_detector",
  "counter",
  "counter_match",
  "llm_eval",
  "document_based_eval",
];

export const getEvaluationToolOptions = (t) =>
  EVALUATION_TOOL_KEYS.map((key) => ({
    value: key,
    name: key,
    label: t(`input.evaluation_tool.${key}.label`),
    tooltip: t(`input.evaluation_tool.${key}.tooltip`),
  }));

export const getEvaluationToolLabel = (tool, t) => {
  if (!tool) {
    return "-";
  }

  const key = `input.evaluation_tool.${tool}.label`;
  const translated = t(key);

  return translated === key ? tool : translated;
};

export const getEvaluationToolTooltip = (tool, t) => {
  if (!tool) {
    return "";
  }

  const key = `input.evaluation_tool.${tool}.tooltip`;
  const translated = t(key);

  return translated === key ? "" : translated;
};

export const DEFAULT_EVALUATION_TOOL = "llm_eval";
export const DEFAULT_CRITERION_WEIGHT = 3;

export const createSubcriterion = () => ({
  id: uuidv7(),
  description: "",
  parameter: "",
  tool: DEFAULT_EVALUATION_TOOL,
  weight: DEFAULT_CRITERION_WEIGHT,
});

export const pixelsToGridH = (px) => {
  const unit = GRID_ROW_HEIGHT + GRID_MARGIN_Y;

  return Math.max(MIN_ITEM_H, Math.ceil((px + GRID_MARGIN_Y) / unit));
};

export const applyRowPositions = (items = [], cols = GRID_COLS) => {
  const rows = [];

  items.forEach((item, index) => {
    const rowIdx = Math.floor(index / cols);

    if (!rows[rowIdx]) {
      rows[rowIdx] = [];
    }

    rows[rowIdx].push(item);
  });

  let currentY = 0;
  const result = [];

  rows.forEach((row) => {
    const maxH = Math.max(
      ...row.map((item) => item.h || DEFAULT_ITEM_H),
      DEFAULT_ITEM_H,
    );

    row.forEach((item, colIndex) => {
      result.push({
        ...item,
        x: colIndex,
        y: currentY,
        h: maxH,
      });
    });

    currentY += maxH;
  });

  return result;
};

export const createLayoutItem = (
  id,
  index,
  cols = GRID_COLS,
  defaultH = DEFAULT_ITEM_H,
) => ({
  i: id,
  x: index % cols,
  y: 0,
  w: 1,
  h: defaultH,
  minW: 1,
  maxW: 1,
});

export const createAddSlotLayoutItem = (
  index,
  layout = [],
  {
    cols = GRID_COLS,
    addSlotId = ADD_SLOT_ID,
    defaultH = DEFAULT_ITEM_H,
  } = {},
) => {
  const cards = applyRowPositions(
    sortLayoutItems(filterCardLayout(layout, addSlotId)),
    cols,
  );
  const rowIdx = Math.floor(index / cols);
  const colIndex = index % cols;

  const rows = [];

  cards.forEach((item, cardIndex) => {
    const cardRowIdx = Math.floor(cardIndex / cols);

    if (!rows[cardRowIdx]) {
      rows[cardRowIdx] = {y: item.y, maxH: item.h};
    } else {
      rows[cardRowIdx].maxH = Math.max(rows[cardRowIdx].maxH, item.h);
    }
  });

  let y = 0;

  for (let row = 0; row < rowIdx; row += 1) {
    y += rows[row]?.maxH ?? defaultH;
  }

  return {
    i: addSlotId,
    x: colIndex,
    y,
    w: 1,
    h: rows[rowIdx]?.maxH ?? defaultH,
    static: true,
  };
};

export const isAddSlotItem = (item, addSlotId = ADD_SLOT_ID) =>
  item?.i === addSlotId;

export const filterCardLayout = (layout = [], addSlotId = ADD_SLOT_ID) =>
  layout.filter((item) => !isAddSlotItem(item, addSlotId));

export const getAddSlotLayoutItem = (index, layout = []) =>
  createAddSlotLayoutItem(index, layout);

export const buildLayoutFromSubcriteria = (subcriteria = []) =>
  applyRowPositions(
    subcriteria.map((item, index) => createLayoutItem(item.id, index)),
  );

export const appendSubcriterion = (subcriteria = [], layout = []) => {
  const item = createSubcriterion();
  const ordered = reorderSubcriteriaByLayout(subcriteria, layout);
  const nextSubcriteria = [...ordered, item];
  const nextLayout = applyRowPositions([
    ...sortLayoutItems(filterCardLayout(layout)),
    {
      i: item.id,
      w: 1,
      h: DEFAULT_ITEM_H,
      minW: 1,
      maxW: 1,
    },
  ]);

  return {subcriteria: nextSubcriteria, layout: nextLayout};
};

export const createCriteriaSection = () => {
  const subcriteria = [createSubcriterion()];

  return {
    id: uuidv7(),
    title: "",
    subcriteria,
    layout: buildLayoutFromSubcriteria(subcriteria),
  };
};

export const sortLayoutItems = (layout = []) =>
  [...layout].sort((a, b) => {
    if (a.y !== b.y) {
      return a.y - b.y;
    }

    return a.x - b.x;
  });

export const applyDynamicHeights = (layout = [], heightsById = {}) => {
  const cards = sortLayoutItems(filterCardLayout(layout));

  const withHeights = cards.map((item) => {
    const px = heightsById[item.i];

    return {
      ...item,
      h: px ? pixelsToGridH(px) : item.h || DEFAULT_ITEM_H,
    };
  });

  return applyRowPositions(withHeights);
};

export const isSameLayout = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  const rightMap = new Map(right.map((item) => [item.i, item]));

  return left.every((item) => {
    const other = rightMap.get(item.i);

    return (
      other && item.x === other.x && item.y === other.y && item.h === other.h
    );
  });
};

export const compactLayout = (layout = [], cols = GRID_COLS) => {
  const sorted = sortLayoutItems(filterCardLayout(layout));

  return applyRowPositions(
    sorted.map((item, index) => ({
      ...item,
      x: index % cols,
    })),
    cols,
  );
};

export const reorderSubcriteriaByLayout = (subcriteria = [], layout = []) => {
  const map = new Map(subcriteria.map((item) => [item.id, item]));

  return sortLayoutItems(filterCardLayout(layout))
    .map((item) => map.get(item.i))
    .filter(Boolean);
};

export const applyLayoutChange = (subcriteria = [], nextLayout = []) => {
  const compacted = compactLayout(nextLayout);

  return {
    subcriteria: reorderSubcriteriaByLayout(subcriteria, compacted),
    layout: compacted,
  };
};

export const buildLayoutFromSections = (sections = []) =>
  applyRowPositions(
    sections.map((section, index) =>
      createLayoutItem(
        section.id,
        index,
        SECTION_GRID_COLS,
        SECTION_DEFAULT_ITEM_H,
      ),
    ),
    SECTION_GRID_COLS,
  );

export const getSectionAddSlotLayoutItem = (index, layout = []) =>
  createAddSlotLayoutItem(index, layout, {
    cols: SECTION_GRID_COLS,
    addSlotId: SECTION_ADD_SLOT_ID,
    defaultH: SECTION_DEFAULT_ITEM_H,
  });

export const filterSectionCardLayout = (layout = []) =>
  filterCardLayout(layout, SECTION_ADD_SLOT_ID);

export const compactSectionLayout = (layout = []) =>
  compactLayout(layout, SECTION_GRID_COLS);

export const reorderSectionsByLayout = (sections = [], layout = []) => {
  const map = new Map(sections.map((section) => [section.id, section]));

  return sortLayoutItems(filterSectionCardLayout(layout))
    .map((item) => map.get(item.i))
    .filter(Boolean);
};

export const applySectionLayoutChange = (target, nextLayout = []) => {
  const compacted = compactSectionLayout(nextLayout);

  return {
    ...target,
    sections: reorderSectionsByLayout(target.sections ?? [], compacted),
    sectionsLayout: compacted,
  };
};

export const normalizeSection = (section) => {
  const subcriteria =
    Array.isArray(section?.subcriteria) && section.subcriteria.length > 0
      ? section.subcriteria
      : [createSubcriterion()];

  const rawLayout =
    Array.isArray(section?.layout) && section.layout.length > 0
      ? section.layout
      : buildLayoutFromSubcriteria(subcriteria);

  const layout =
    rawLayout.length === subcriteria.length
      ? compactLayout(rawLayout)
      : buildLayoutFromSubcriteria(subcriteria);

  return {
    id: section?.id ?? uuidv7(),
    title: section?.title ?? "",
    subcriteria: reorderSubcriteriaByLayout(subcriteria, layout),
    layout,
  };
};

export const normalizeSections = (target) => {
  if (Array.isArray(target?.sections) && target.sections.length > 0) {
    return target.sections.map(normalizeSection);
  }

  return [
    normalizeSection({
      id: uuidv7(),
      title: target?.title ?? "",
      subcriteria: target?.subcriteria,
      layout: target?.layout,
    }),
  ];
};

export const normalizeCriteriaTarget = (target) => {
  const sections = normalizeSections(target);
  const rawSectionsLayout =
    Array.isArray(target?.sectionsLayout) && target.sectionsLayout.length > 0
      ? target.sectionsLayout
      : buildLayoutFromSections(sections);

  const sectionsLayout =
    rawSectionsLayout.length === sections.length
      ? compactSectionLayout(rawSectionsLayout)
      : buildLayoutFromSections(sections);

  return {
    name: target?.name ?? "",
    sections: reorderSectionsByLayout(sections, sectionsLayout),
    sectionsLayout,
  };
};

export const mapEvaluationTemplateToTarget = (template) => {
  const structure = Array.isArray(template?.structure) ? template.structure : [];

  const sections = structure.map((section) => {
    const subcriteria = (section.criteria ?? []).map((criterion) => ({
      id: String(criterion.id ?? uuidv7()),
      description: criterion.desc ?? "",
      parameter: criterion.params ?? "",
      tool: criterion.tool ?? "",
      weight: Number(criterion.weight) || DEFAULT_CRITERION_WEIGHT,
    }));

    return {
      id: String(section.id ?? uuidv7()),
      title: section.name ?? "",
      subcriteria,
      layout: buildLayoutFromSubcriteria(subcriteria),
    };
  });

  return {
    id: template?.id,
    name: template?.name ?? "",
    sections,
    sectionsLayout: buildLayoutFromSections(sections),
    created_at: template?.created_at,
    updated_at: template?.updated_at,
  };
};

export const mapTargetToEvaluationTemplate = (target) => {
  const normalized = normalizeCriteriaTarget(target ?? {});
  const sections = reorderSectionsByLayout(
    normalized.sections,
    normalized.sectionsLayout,
  );

  const mapCriterionPayload = (subcriterion) => {
    const parameterValue = subcriterion.parameter?.trim();

    const payload = {
      id: String(subcriterion.id),
      desc: subcriterion.description.trim(),
      tool: subcriterion.tool.trim(),
      params: parameterValue || null,
      weight: parseInt(subcriterion.weight, 10) || DEFAULT_CRITERION_WEIGHT,
    };

    return payload;
  };

  const mapSectionPayload = (section) => ({
    id: String(section.id),
    name: section.title.trim(),
    criteria: reorderSubcriteriaByLayout(
      section.subcriteria,
      section.layout,
    ).map(mapCriterionPayload),
  });

  return {
    name: normalized.name.trim(),
    structure: sections.map(mapSectionPayload),
  };
};

export const serializeCriteria = (target) => ({
  name: target?.name?.trim() || "",
  sections: (target?.sections ?? []).map((section) => ({
    id: section.id,
    title: section.title?.trim() || "",
    subcriteria: section.subcriteria ?? [],
    layout: section.layout ?? [],
  })),
  sectionsLayout: target?.sectionsLayout ?? [],
});

export const getCriteriaComparableState = (target) => {
  const normalized = normalizeCriteriaTarget(target ?? {});

  return {
    name: normalized.name.trim(),
    sections: reorderSectionsByLayout(
      normalized.sections,
      normalized.sectionsLayout,
    ).map((section) => ({
      title: section.title.trim(),
      subcriteria: reorderSubcriteriaByLayout(
        section.subcriteria,
        section.layout,
      ).map((subcriterion) => ({
        description: subcriterion.description.trim(),
        parameter: subcriterion.parameter.trim(),
        tool: subcriterion.tool.trim(),
        weight: subcriterion.weight ?? DEFAULT_CRITERION_WEIGHT,
      })),
    })),
  };
};

const areCriteriaStatesEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

export const hasCriteriaUserContent = (target) => {
  const state = getCriteriaComparableState(target);

  if (state.name) {
    return true;
  }

  if (state.sections.length > 1) {
    return true;
  }

  return state.sections.some((section) => {
    if (section.title) {
      return true;
    }

    if (section.subcriteria.length > 1) {
      return true;
    }

    return section.subcriteria.some(
      (subcriterion) =>
        subcriterion.description ||
        subcriterion.parameter ||
        (subcriterion.tool && subcriterion.tool !== DEFAULT_EVALUATION_TOOL) ||
        (subcriterion.weight != null &&
          Number(subcriterion.weight) !== DEFAULT_CRITERION_WEIGHT),
    );
  });
};

export const isCriteriaTargetDirty = (target, baseline) =>
  !areCriteriaStatesEqual(
    getCriteriaComparableState(target),
    getCriteriaComparableState(baseline),
  );

export const addCriteriaSection = (target) => {
  const section = createCriteriaSection();
  const nextSections = [...(target?.sections ?? []), section];

  return {
    ...target,
    sections: nextSections,
    sectionsLayout: buildLayoutFromSections(nextSections),
  };
};

export const removeCriteriaSection = (target, sectionId) => {
  if ((target?.sections ?? []).length <= 1) {
    return target;
  }

  const nextSections = target.sections.filter(
    (section) => section.id !== sectionId,
  );

  return {
    ...target,
    sections: nextSections,
    sectionsLayout: buildLayoutFromSections(nextSections),
  };
};

export const updateCriteriaSection = (target, sectionId, patch) => ({
  ...target,
  sections: (target?.sections ?? []).map((section) =>
    section.id === sectionId ? {...section, ...patch} : section,
  ),
});

export const updateSubcriterionField = (
  target,
  sectionId,
  subcriterionId,
  field,
  value,
) => ({
  ...target,
  sections: (target?.sections ?? []).map((section) =>
    section.id === sectionId
      ? {
          ...section,
          subcriteria: section.subcriteria.map((item) =>
            item.id === subcriterionId ? {...item, [field]: value} : item,
          ),
        }
      : section,
  ),
});

export const removeSubcriterion = (target, sectionId, subcriterionId) => ({
  ...target,
  sections: (target?.sections ?? []).map((section) => {
    if (section.id !== sectionId) {
      return section;
    }

    const nextSubcriteria = section.subcriteria.filter(
      (item) => item.id !== subcriterionId,
    );
    const nextLayout = buildLayoutFromSubcriteria(nextSubcriteria);

    return {
      ...section,
      subcriteria: nextSubcriteria,
      layout: nextLayout,
    };
  }),
});
